// 상품 가격 단위(units) 도메인.
// 단위는 DB(units 테이블)에서 관리되며 관리자가 등록/수정/삭제한다(마이그 0022).
//  - 본 파일은 단위 "코드" 타입, 기본 시드 코드 참조 상수, 검증 스키마, 그리고 단위 목록을 받아
//    화면 라벨을 만드는 순수 헬퍼를 제공한다. 라벨/중량환산 값 자체는 항상 DB 에서 받아온 UnitDto 에서 읽는다.
//  - 사용자/관리자 프런트는 단위 목록을 1회 조회해 캐시하고, 본 헬퍼로 표시 문자열을 만든다.

import { z } from "zod";

// 기본 제공(시드) 단위 코드. 신규 단위는 관리자가 DB 에 추가하므로 본 상수는 "기본 5종" 참조용이다.
//  - 'pack' 은 1팩 중량(products.pack_weight_g) 특수 표기, 'each' 는 "n개" 표기에 코드로 분기한다.
export const PRODUCT_UNIT = {
  G100: "g100",
  GEUN: "geun",
  PACK: "pack",
  KG: "kg",
  EACH: "each",
} as const;

// 단위 코드 타입.
// 단위가 DB 관리로 승격되어 임의 코드가 가능하므로 string 별칭으로 둔다(기존 호출부 호환 유지).
export type ProductUnit = string;

// 단위 코드 형식. 식별자이므로 보수적으로 제한한다(영문 소문자로 시작, 소문자/숫자/밑줄).
export const unitCodeSchema = z
  .string()
  .trim()
  .regex(
    /^[a-z][a-z0-9_]{0,23}$/u,
    "단위 코드는 영문 소문자로 시작하고 소문자/숫자/밑줄만 사용할 수 있습니다.",
  );

const UNIT_LABEL_MAX = 20;

// 단위 응답 DTO. 사용자/관리자 공용(사용자에는 is_active=1 만 내려보낼 수 있다).
export interface UnitDto {
  code: string;
  label: string;
  // 1단위의 기준 중량(g). 무게가변 단위만 값을 가지며 그 외는 null. 실중량 환산(PR-B)에 사용.
  baseGrams: number | null;
  // 실중량 입력 대상 단위인지의 기본값(상품별로 덮어쓸 수 있음).
  weightVariable: boolean;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// 관리자: 단위 생성 입력.
export const unitCreateInputSchema = z.object({
  code: unitCodeSchema,
  label: z.string().trim().min(1, "단위 이름을 입력하세요.").max(UNIT_LABEL_MAX),
  baseGrams: z.number().int().positive().max(1_000_000).nullable().optional(),
  weightVariable: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
});
export type UnitCreateInput = z.infer<typeof unitCreateInputSchema>;

// 관리자: 단위 수정 입력. 코드는 식별자라 불변이므로 제외한다(라벨/중량/순서/활성만 수정 가능).
export const unitUpdateInputSchema = z
  .object({
    label: z.string().trim().min(1, "단위 이름을 입력하세요.").max(UNIT_LABEL_MAX),
    baseGrams: z.number().int().positive().max(1_000_000).nullable(),
    weightVariable: z.boolean(),
    sortOrder: z.number().int().min(0),
    isActive: z.boolean(),
  })
  .partial();
export type UnitUpdateInput = z.infer<typeof unitUpdateInputSchema>;

// 단위 코드 -> UnitDto 빠른 조회 맵.
export function indexUnitsByCode(units: UnitDto[]): Map<string, UnitDto> {
  const map = new Map<string, UnitDto>();
  for (const u of units) {
    map.set(u.code, u);
  }
  return map;
}

// 단위 목록(또는 맵)에서 표시 라벨을 찾는다.
//  - 단위가 '팩'이고 packWeightG 가 있으면 "팩(500g)" 형태로 표시한다.
//  - 목록에 없는 코드(삭제된 단위 등)는 코드를 그대로 폴백 표시한다(화면이 깨지지 않게).
export function resolveUnitLabel(
  units: UnitDto[] | Map<string, UnitDto>,
  code: string,
  packWeightG?: number | null,
): string {
  const map = units instanceof Map ? units : indexUnitsByCode(units);
  const base = map.get(code)?.label ?? code;
  if (code === PRODUCT_UNIT.PACK && packWeightG && packWeightG > 0) {
    return `${base}(${packWeightG.toLocaleString()}g)`;
  }
  return base;
}

// 단위 + 수량을 손님 화면 표시 문자열로 변환한다.
// 예) g100 × 3 -> "100g x 3 (300g)" / geun × 2 -> "근 x 2 (1.2kg)" / each × 4 -> "4개"
// 표시 의도:
//   - 무게가변 단위(base_grams 보유)는 총 중량을 괄호로 덧붙여 정확한 양을 명시한다.
//   - 마리/개(each)는 "n개" 한 줄로 짧게.
//   - 그 외(팩 등 고정 단위)는 "라벨 x n".
export function formatCartLineLabel(
  units: UnitDto[] | Map<string, UnitDto>,
  code: string,
  quantity: number,
): string {
  const map = units instanceof Map ? units : indexUnitsByCode(units);
  if (code === PRODUCT_UNIT.EACH) {
    return `${quantity}개`;
  }
  const unit = map.get(code);
  const label = unit?.label ?? code;
  const baseGrams = unit?.baseGrams ?? null;
  if (baseGrams && baseGrams > 0) {
    return `${label} x ${quantity} (${formatWeightG(baseGrams * quantity)})`;
  }
  return `${label} x ${quantity}`;
}

// 중량(g)을 사람이 읽는 문자열로. 1kg 이상이면 kg 로 환산한다. 예) 300 -> "300g", 1500 -> "1.5kg"
//  - 추가 옵션 도입으로 "총 중량"을 여러 화면(담기 모달/장바구니/주문/영수증)에서 표시하게 되어 공용 헬퍼로 분리했다.
export function formatWeightG(totalG: number): string {
  return totalG >= 1000 ? `${(totalG / 1000).toLocaleString()}kg` : `${totalG}g`;
}

// 단위 값이 비어있지 않은 문자열인지 검사하는 타입 가드. 실제 코드 존재 여부는 API 가 DB 로 검증한다.
export function isProductUnit(value: unknown): value is ProductUnit {
  return typeof value === "string" && value.length > 0;
}
